import React, { useState, useEffect } from 'react';
import { Card, Button, Spin, Row, Col, Table, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../../services/analyticsService';
import { BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import './Analytics.css';

const COLORS = ['#05668D', '#028090', '#00A896', '#02C39A', '#F0F3BD', '#05668D'];

function Analytics() {
    const { t } = useTranslation();
    const [period, setPeriod] = useState('month');
    const [dashboard, setDashboard] = useState(null);
    const [diseaseStats, setDiseaseStats] = useState([]);
    const [drugStats, setDrugStats] = useState([]);
    const [testStats, setTestStats] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAnalytics();
    }, [period]);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
            const [dashboardData, diseases, drugs, tests] = await Promise.all([
                analyticsService.getDashboard(period),
                analyticsService.getDiseaseStatistics({ limit: 10 }),
                analyticsService.getDrugAnalytics({ period }),
                analyticsService.getTestAnalytics({})
            ]);

            setDashboard(dashboardData);
            setDiseaseStats(diseases);
            setDrugStats(drugs);
            setTestStats(tests);
        } catch (error) {
            console.error('Error loading analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const diseaseColumns = [
        {
            title: t('disease'),
            dataIndex: 'disease',
            key: 'disease',
        },
        {
            title: t('patientCount'),
            dataIndex: 'patientCount',
            key: 'patientCount',
        },
        {
            title: t('visitCount'),
            dataIndex: 'visitCount',
            key: 'visitCount',
        },
    ];

    if (loading) {
        return (
            <div className="page-container">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" tip={t('loadingAnalytics')} />
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">{t('analyticsDashboard')}</h1>
                <Space>
                    <Button
                        type={period === 'today' ? 'primary' : 'default'}
                        onClick={() => setPeriod('today')}
                    >
                        {t('today')}
                    </Button>
                    <Button
                        type={period === 'week' ? 'primary' : 'default'}
                        onClick={() => setPeriod('week')}
                    >
                        {t('week')}
                    </Button>
                    <Button
                        type={period === 'month' ? 'primary' : 'default'}
                        onClick={() => setPeriod('month')}
                    >
                        {t('month')}
                    </Button>
                    <Button
                        type={period === 'year' ? 'primary' : 'default'}
                        onClick={() => setPeriod('year')}
                    >
                        {t('year')}
                    </Button>
                </Space>
            </div>

            {/* Key Metrics */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="metric-card">
                        <div className="metric-icon">👥</div>
                        <div className="metric-value">{dashboard?.uniquePatients || 0}</div>
                        <div className="metric-label">{t('patients')}</div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="metric-card">
                        <div className="metric-icon">📋</div>
                        <div className="metric-value">{dashboard?.totalVisits || 0}</div>
                        <div className="metric-label">{t('visits')}</div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="metric-card">
                        <div className="metric-icon">💰</div>
                        <div className="metric-value">{dashboard?.totalRevenue?.toLocaleString() || 0} IQD</div>
                        <div className="metric-label">{t('revenue')}</div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="metric-card">
                        <div className="metric-icon">🔬</div>
                        <div className="metric-value">{dashboard?.testsOrdered || 0}</div>
                        <div className="metric-label">{t('testsOrdered')}</div>
                    </Card>
                </Col>
            </Row>

            {/* Charts */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                    <Card className="chart-card">
                        <h2>{t('diseaseDistribution')} (Top 10)</h2>
                        {diseaseStats.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={diseaseStats}
                                        dataKey="patientCount"
                                        nameKey="disease"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={100}
                                        label={(entry) => `${entry.disease}: ${entry.patientCount}`}
                                    >
                                        {diseaseStats.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="no-data">{t('noDataAvailable')}</p>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card className="chart-card">
                        <h2>{t('topPrescribedDrugs')}</h2>
                        {drugStats.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={drugStats.slice(0, 10)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="totalPrescriptions" fill="#05668D" name={t('prescriptions')} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="no-data">{t('noDataAvailable')}</p>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Test Statistics */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24}>
                    <Card className="chart-card">
                        <h2>{t('testStatistics')}</h2>
                        {testStats.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={testStats}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="testType" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="totalOrdered" fill="#05668D" name={t('totalOrdered')} />
                                    <Bar dataKey="completed" fill="#02C39A" name={t('completed')} />
                                    <Bar dataKey="abnormal" fill="#FF8042" name={t('abnormal')} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="no-data">{t('noDataAvailable')}</p>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Disease Details Table */}
            <Card className="table-card">
                <h2>{t('diseaseStatisticsDetails')}</h2>
                <Table
                    columns={diseaseColumns}
                    dataSource={diseaseStats}
                    rowKey={(record, index) => index}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: t('noDataAvailable') }}
                />
            </Card>
        </div>
    );
}

export default Analytics;
